import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircleCheck,
  CircleOff,
  CreditCard,
  Plus,
  PlusCircle,
  RotateCw,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getApiErrorCode, getApiErrorMessage } from "@/services/apiClient";
import paymentService from "@/services/paymentService";
import { showError, showInfo } from "@/store/errorStore";
import PaymentMethodCard from "./PaymentMethodCard";
import PaymentMethodFormModal from "./PaymentMethodFormModal";
import { isActive, normalize } from "./paymentUtils";

const TITLE = "Meios de Pagamento";

// Criados automaticamente pela API em toda empresa nova
const DEFAULT_KEYS = new Set(["pix", "cartao_credito", "cartao_debito", "dinheiro", "pagamento_local"]);

const FILTERS = {
  all: { label: "Todos", match: () => true },
  active: { label: "Ativos", match: (p) => isActive(p) },
  inactive: { label: "Inativos", match: (p) => !isActive(p) },
};

// Erros de negócio (400) tratados na tela: mensagem própria, sem o modal global
const SILENT_400 = { silentStatuses: [400] };

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Erros HTTP já abrem o modal global; aqui só falhas locais (sem resposta da API) */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, TITLE);
  }
};

function MetricCard({ label, icon: Icon, iconClass, onClick, active, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={cn(
        "flex min-w-0 flex-col justify-between rounded-xl border bg-card p-3.5 text-left shadow-soft transition-all",
        onClick && "hover:shadow-ambient active:scale-[0.99]",
        active && "border-primary ring-2 ring-primary/15"
      )}
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full", iconClass)}>
          <Icon className="h-4 w-4" />
        </span>
      </span>
      <span className="mt-2 block min-w-0">{children}</span>
    </Tag>
  );
}

function FilterChip({ active, onClick, count, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold shadow-sm transition-colors",
        active ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
      <span
        className={cn(
          "flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px]",
          active ? "bg-white/20" : "bg-surface-container"
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Meios de pagamento da empresa via /payments (CRUD + ativar/desativar).
 * O `id` é a chave textual enviada no pedido. Meio já usado em pedidos não
 * pode ser excluído (PAY005) nem trocar de chave (PAY006): a tela sugere
 * desativar.
 */
function PaymentMethodsManagement() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Meio usado em pedidos (PAY005): oferece desativar no lugar de excluir
  const [inUseTarget, setInUseTarget] = useState(null);

  /**
   * `background`: recarrega sem esqueleto (após salvar/excluir); se falhar,
   * o modal global avisa e a lista atual continua na tela. Na carga normal o
   * erro aparece no lugar da lista, com "Tentar novamente".
   */
  const loadData = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const data = await paymentService.getAll({}, { silent: !background });
      setPayments(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      if (!background) setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Texto de busca calculado uma vez por carga
  const entries = useMemo(
    () => payments.map((payment) => ({ payment, search: normalize(`${payment.name} ${payment.id}`) })),
    [payments]
  );

  const stats = useMemo(() => {
    const active = payments.filter(isActive).length;
    return {
      total: payments.length,
      active,
      inactive: payments.length - active,
      custom: payments.filter((p) => !DEFAULT_KEYS.has(normalize(p.id))).length,
    };
  }, [payments]);

  // Busca: base das contagens dos chips e da lista
  const scoped = useMemo(() => {
    const term = normalize(searchQuery);
    return term ? entries.filter((e) => e.search.includes(term)) : entries;
  }, [entries, searchQuery]);

  const counts = useMemo(() => {
    const c = {};
    Object.entries(FILTERS).forEach(([key, f]) => {
      c[key] = scoped.filter((e) => f.match(e.payment)).length;
    });
    return c;
  }, [scoped]);

  const shown = useMemo(
    () => scoped.filter((e) => (FILTERS[filter] || FILTERS.all).match(e.payment)).map((e) => e.payment),
    [scoped, filter]
  );

  const hasFilters = Boolean(searchQuery.trim()) || filter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setFilter("all");
  };

  const toggleFilter = (key) => setFilter((current) => (current === key ? "all" : key));

  /* ---------- Ações ---------- */

  const openCreate = () => {
    setEditingItem(null);
    setServerErrors(null);
    setShowModal(true);
  };

  const openEdit = useCallback((payment) => {
    setEditingItem(payment);
    setServerErrors(null);
    setShowModal(true);
  }, []);

  // editingItem fica até a próxima abertura: limpar aqui trocaria o título
  // durante a animação de fechamento
  const handleModalChange = (open) => setShowModal(open);

  const handleSave = async (payload) => {
    setSaving(true);
    setServerErrors(null);
    try {
      if (editingItem) {
        await paymentService.update(editingItem.id, payload, SILENT_400);
        showInfo(`"${payload.name}" atualizado`, TITLE);
      } else {
        await paymentService.create(payload, SILENT_400);
        showInfo(`"${payload.name}" adicionado`, TITLE);
      }
      handleModalChange(false);
      await loadData({ background: true });
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === "PAY003") {
        setServerErrors({ id: "Já existe um meio de pagamento com essa chave nesta empresa" });
      } else if (code === "PAY006") {
        setServerErrors({
          id: `Este meio já foi usado em pedidos: mantenha a chave "${editingItem?.id}".`,
        });
      } else if (error?.response?.status === 400) {
        showError(getApiErrorMessage(error) || "Dados inválidos", TITLE);
      } else if (error?.response?.status === 404) {
        // Registro sumiu (excluído em outra sessão): fecha e atualiza a lista
        handleModalChange(false);
        await loadData({ background: true });
      } else {
        showLocalError(error, "Erro ao salvar o meio de pagamento");
      }
    } finally {
      setSaving(false);
    }
  };

  const setActive = useCallback(
    async (payment, active) => {
      setTogglingId(payment.id);
      try {
        await paymentService.setActive(payment.id, active);
        showInfo(
          active ? `"${payment.name}" foi ativado` : `"${payment.name}" foi desativado`,
          TITLE
        );
        await loadData({ background: true });
      } catch (error) {
        showLocalError(error, "Erro ao alterar o status");
        if (error?.response?.status === 404) await loadData({ background: true });
      } finally {
        setTogglingId(null);
      }
    },
    [loadData]
  );

  // Desativar pede confirmação (some do checkout); ativar é imediato
  const handleToggleActive = useCallback(
    (payment, active) => {
      if (active) setActive(payment, true);
      else setDeactivateTarget(payment);
    },
    [setActive]
  );

  const confirmDeactivate = () => {
    const target = deactivateTarget;
    setDeactivateTarget(null);
    if (target) setActive(target, false);
  };

  const handleDelete = useCallback((payment) => setDeleteTarget(payment), []);

  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    setDeletingId(target.id);
    try {
      await paymentService.delete(target.id, SILENT_400);
      showInfo(`"${target.name}" foi excluído`, TITLE);
      await loadData({ background: true });
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === "PAY005") {
        setInUseTarget(target);
      } else if (error?.response?.status === 400) {
        showError(getApiErrorMessage(error) || "Não foi possível excluir", TITLE);
      } else {
        showLocalError(error, "Erro ao excluir o meio de pagamento");
        if (error?.response?.status === 404) await loadData({ background: true });
      }
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDeactivateInUse = () => {
    const target = inUseTarget;
    setInUseTarget(null);
    if (target && isActive(target)) setActive(target, false);
  };

  const initialLoading = loading && payments.length === 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título e ação principal */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-bold tracking-tight text-primary md:text-2xl">
            {TITLE}
          </h1>
          <p className="truncate text-xs text-muted-foreground md:text-sm">
            {initialLoading
              ? "Carregando meios de pagamento..."
              : plural(stats.total, "meio cadastrado", "meios cadastrados")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Novo
        </button>
      </div>

      {/* Métricas (2x2 no celular, 4 colunas no desktop) */}
      {initialLoading ? (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <MetricCard label="Total" icon={CreditCard} iconClass="bg-surface-container text-primary">
            <span className="block font-display text-xl font-bold text-foreground">{stats.total}</span>
            <span className="block truncate text-[11px] text-muted-foreground">Meios cadastrados</span>
          </MetricCard>
          <MetricCard
            label="Ativos"
            icon={CircleCheck}
            iconClass="bg-secondary text-primary"
            onClick={() => toggleFilter("active")}
            active={filter === "active"}
          >
            <span className="block font-display text-xl font-bold text-primary">{stats.active}</span>
            <span className="block truncate text-[11px] font-medium text-primary">Aceitos nos pedidos</span>
          </MetricCard>
          <MetricCard
            label="Inativos"
            icon={CircleOff}
            iconClass="bg-terra-soft text-terra"
            onClick={() => toggleFilter("inactive")}
            active={filter === "inactive"}
          >
            <span className="block font-display text-xl font-bold text-terra">{stats.inactive}</span>
            <span className="block truncate text-[11px] font-medium text-terra">Fora do checkout</span>
          </MetricCard>
          <MetricCard label="Personalizados" icon={Sparkles} iconClass="bg-surface-container text-primary">
            <span className="block font-display text-xl font-bold text-foreground">{stats.custom}</span>
            <span className="block truncate text-[11px] text-muted-foreground">Além dos 5 padrão</span>
          </MetricCard>
        </div>
      )}

      {/* Busca */}
      <div className="relative flex items-center">
        <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Pesquisar por nome ou chave..."
          aria-label="Pesquisar meios de pagamento"
          className="h-11 w-full rounded-xl border border-border bg-card pl-10 pr-10 text-base text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 md:text-sm"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            aria-label="Limpar busca"
            className="absolute right-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Chips de filtro (scroll horizontal no celular) */}
      <div
        className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0"
        role="group"
        aria-label="Filtrar meios de pagamento"
      >
        {Object.entries(FILTERS).map(([key, f]) => (
          <FilterChip key={key} active={filter === key} count={counts[key] || 0} onClick={() => setFilter(key)}>
            {f.label}
          </FilterChip>
        ))}
      </div>

      {/* Lista */}
      {initialLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[118px] rounded-2xl" />
          ))}
        </div>
      ) : loadError && payments.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <CreditCard className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            Não foi possível carregar os meios de pagamento
          </p>
          <p className="mt-1 text-sm text-muted-foreground">Verifique a conexão e tente de novo.</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-4 flex h-9 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <RotateCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <CreditCard className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            {hasFilters ? "Nenhum meio de pagamento encontrado" : "Nenhum meio de pagamento cadastrado"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasFilters
              ? "Tente outra busca ou limpe os filtros."
              : "Cadastre as formas de pagamento que o cliente pode escolher no pedido."}
          </p>
          <button
            type="button"
            onClick={hasFilters ? clearFilters : openCreate}
            className={cn(
              "mt-4 flex h-9 items-center gap-1 rounded-full px-3.5 text-sm font-semibold transition-colors",
              hasFilters
                ? "border border-border bg-surface text-primary hover:bg-muted"
                : "bg-primary text-primary-foreground hover:bg-brand-dark"
            )}
          >
            {hasFilters ? <X className="h-4 w-4" /> : <PlusCircle className="h-4 w-4" />}
            {hasFilters ? "Limpar filtros" : "Novo meio de pagamento"}
          </button>
        </div>
      ) : (
        <section className="space-y-3" aria-label="Lista de meios de pagamento">
          <div className="flex items-center justify-between gap-3 px-1 text-xs text-muted-foreground">
            <span>Formas aceitas no pedido</span>
            <span className="flex items-center gap-2">
              Mostrando {plural(shown.length, "item", "itens")}
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[13px] font-semibold text-primary hover:underline"
                >
                  Limpar filtros
                </button>
              )}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {shown.map((payment) => (
              <PaymentMethodCard
                key={payment.id}
                payment={payment}
                editing={showModal && editingItem?.id === payment.id}
                toggling={togglingId === payment.id}
                deleting={deletingId === payment.id}
                onEdit={openEdit}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </section>
      )}

      <PaymentMethodFormModal
        open={showModal}
        onOpenChange={handleModalChange}
        payment={editingItem}
        payments={payments}
        serverErrors={serverErrors}
        onSave={handleSave}
        loading={saving}
      />

      {/* Desativar */}
      <AlertDialog open={Boolean(deactivateTarget)} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar meio de pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{deactivateTarget?.name}</span> deixará de
              aparecer no cardápio e não poderá ser usado em novos pedidos. Você pode ativá-lo de novo a
              qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivate}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excluir */}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir meio de pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{deleteTarget?.name}</span> será removido
              definitivamente. Só é possível excluir um meio que nunca foi usado em pedidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PAY005: usado em pedidos */}
      <AlertDialog open={Boolean(inUseTarget)} onOpenChange={(open) => !open && setInUseTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Não é possível excluir</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{inUseTarget?.name}</span> já foi usado em
              pedidos e precisa ser mantido no histórico.{" "}
              {isActive(inUseTarget)
                ? "Desative-o para que não seja mais oferecido aos clientes."
                : "Ele já está desativado e não aparece para os clientes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {isActive(inUseTarget) ? (
              <>
                <AlertDialogCancel className="rounded-full">Manter ativo</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDeactivateInUse}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-brand-dark"
                >
                  Desativar
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogCancel className="rounded-full">Entendi</AlertDialogCancel>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PaymentMethodsManagement;
